import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Mail, Globe, Users, HandHeart } from 'lucide-react';
import { motion } from 'framer-motion';

const Support = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const handleDonateClick = () => {
    window.open('https://christianscaringforegypt.org/', '_blank');
  };

  const handleEmailClick = () => {
    window.open('mailto:info@praisesawa.com?subject=Support%20PraiseSawa%20Ministry', '_blank');
  };

  return (
    <div 
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`min-h-screen bg-white py-8 px-4 ${
        isRTL ? 'font-[Tajawal]' : ''
      }`}
      style={isRTL ? { fontFamily: 'Tajawal, Amiri, Arial, sans-serif' } : {}}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            {t('support.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {t('support.subtitle')}
          </p>
        </motion.div>

        {/* Support Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Option 1: Donate via Christians Caring for Egypt */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 20 : -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl shadow-xl p-8 transition-all duration-300 flex flex-col"
          >
            <div className="flex items-center mb-6">
              <div className={`w-12 h-12 min-w-12 min-h-12 max-w-12 max-h-12 bg-[#1c1917] rounded-full flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
                <Globe className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                {t('support.options.donate.title')}
              </h3>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
              {t('support.options.donate.description')}
            </p>
            <div className="relative group">
              {/* Gradient glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-600 to-yellow-500 rounded-xl blur opacity-0 group-hover:opacity-75 transition duration-500"></div>

              <button
                onClick={handleDonateClick}
                className="relative w-full bg-[#1c1917] hover:bg-[#2d2a27] text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl mt-auto hover:scale-105"
              >
                <ExternalLink className="w-5 h-5" />
                {t('support.options.donate.button')}
              </button>
            </div>
          </motion.div>

          {/* Option 2: Contact via Email */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 transition-all duration-300 flex flex-col"
          >
            <div className="flex items-center mb-6">
              <div className={`w-12 h-12 min-w-12 min-h-12 max-w-12 max-h-12 bg-[#1c1917] rounded-full flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
                <Mail className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">
                {t('support.options.contact.title')}
              </h3>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed flex-grow">
              {t('support.options.contact.description')}
            </p>
            <div className="relative group">
              {/* Gradient glow effect */}
              <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 via-orange-600 to-yellow-500 rounded-xl blur opacity-0 group-hover:opacity-75 transition duration-500"></div>

              <button
                onClick={handleEmailClick}
                className="relative w-full bg-[#1c1917] hover:bg-[#2d2a27] text-white py-4 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl mt-auto hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                {t('support.options.contact.button')}
              </button>
            </div>
          </motion.div>
        </div>

        {/* Thank You Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-8"
        >
          <p className="text-lg text-gray-600 italic">
            {t('support.thankYou')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Support;
